import React from 'react';
import { Milestone, Contract } from '../../types/contract';
import { StatusBadge } from './StatusBadge';
import {
  milestoneStatusConfig,
  formatCurrency,
  formatDate,
  isOverdue,
  canStartMilestone,
  canSubmitDeliverable,
  canApproveMilestone,
  canRequestRevision,
} from '../../utils/contractHelpers';
import { Calendar, DollarSign, Upload, CheckCircle, XCircle, AlertCircle, FileText, Play, Clock } from 'lucide-react';
import { Button } from '../UI/Button';

interface PendingExtension {
  amendmentId: string;
  proposedBy: string;
  oldCompletionDate: number;
  newCompletionDate: number;
  reason?: string;
  createdAt: number;
}

interface MilestoneCardProps {
  milestone: Milestone;
  contract: Contract;
  currentUserId: string;
  onStartMilestone?: (milestoneId: string) => void;
  onSubmitDeliverable?: (milestoneId: string) => void;
  onApproveMilestone?: (milestoneId: string) => void;
  onRequestRevision?: (milestoneId: string) => void;
  onCancelMilestone?: (milestoneId: string) => void;
  onRequestExtension?: (milestoneId: string) => void;
  pendingExtension?: PendingExtension;
  onApproveExtension?: (amendmentId: string) => void;
  onRejectExtension?: (amendmentId: string) => void;
}

export const MilestoneCard: React.FC<MilestoneCardProps> = ({
  milestone,
  contract,
  currentUserId,
  onStartMilestone,
  onSubmitDeliverable,
  onApproveMilestone,
  onRequestRevision,
  onRequestExtension,
  pendingExtension,
  onApproveExtension,
  onRejectExtension,
}) => {
  const isDeadlineOverdue = isOverdue(milestone.estimated_completion_date);
  const statusConfig = milestoneStatusConfig[milestone.status];

  const canStart = canStartMilestone(milestone, contract, currentUserId);
  const canSubmit = canSubmitDeliverable(milestone, contract, currentUserId);
  const canApprove = canApproveMilestone(milestone, contract, currentUserId);
  const canReqRevision = canRequestRevision(milestone, contract, currentUserId);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-600 text-sm font-semibold">
              {milestone.order_index}
            </span>
            <h3 className="text-lg font-semibold text-slate-900">{milestone.title}</h3>
          </div>
          <p className="text-sm text-slate-600 ml-11">{milestone.description}</p>
        </div>
        <StatusBadge config={statusConfig} size="sm" />
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-3 gap-4 mb-4 ml-11">
        {/* Payment */}
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-slate-400" />
          <div>
            <p className="text-xs text-slate-500">支付金额</p>
            <p className="text-sm font-semibold text-slate-900">
              {formatCurrency(milestone.payment_amount, contract.currency)}
            </p>
          </div>
        </div>

        {/* Due Date */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <div>
            <p className="text-xs text-slate-500">截止日期</p>
            <p className={`text-sm font-semibold ${isDeadlineOverdue && milestone.status !== 'COMPLETED' && milestone.status !== 'CANCELLED' ? 'text-red-600' : 'text-slate-900'}`}>
              {formatDate(milestone.estimated_completion_date)}
            </p>
          </div>
        </div>

        {/* Submission Count */}
        <div className="flex items-center gap-2">
          <Upload className="w-4 h-4 text-slate-400" />
          <div>
            <p className="text-xs text-slate-500">提交次数</p>
            <p className="text-sm font-semibold text-slate-900">{milestone.submission_count} 次</p>
          </div>
        </div>

        {/* Penalty Info */}
        {milestone.has_penalty && (
          <div className="flex items-center gap-2 col-span-3 pt-2 border-t border-slate-100 mt-2 text-amber-600">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <div className="text-xs">
              <span className="font-medium">逾期惩罚：</span>
              {milestone.penalty_type === 'DAILY'
                ? `${milestone.penalty_value}% / 天`
                : `${formatCurrency(milestone.penalty_value || 0, contract.currency)} (固定)`}
              {milestone.penalty_description && (
                <span className="ml-1 text-slate-500">({milestone.penalty_description})</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Deliverables */}
      {milestone.deliverable_urls.length > 0 && (
        <div className="mb-4 ml-11">
          <p className="text-xs text-slate-500 mb-2">交付物：</p>
          <div className="space-y-2">
            {milestone.deliverable_urls.map((url, index) => {
              const baseUrl = url.split('?')[0];
              const rawName = baseUrl.split('/').pop() || `文件 ${index + 1}`;
              let displayName = rawName;
              try { displayName = decodeURIComponent(rawName); } catch { /* keep raw */ }
              return (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  title={displayName}
                >
                  <FileText className="w-4 h-4 shrink-0" />
                  <span className="truncate">{displayName}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Revision Feedback */}
      {milestone.revision_feedback && (
        <div className="mb-4 ml-11 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-medium text-amber-900 mb-1">修改意见：</p>
              <p className="text-sm text-amber-800 whitespace-pre-wrap">{milestone.revision_feedback}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 ml-11">
        {canStart && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onStartMilestone?.(milestone.milestone_id)}
          >
            <Play className="w-4 h-4 mr-1" />
            开始制作
          </Button>
        )}

        {canSubmit && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onSubmitDeliverable?.(milestone.milestone_id)}
          >
            <Upload className="w-4 h-4 mr-1" />
            {milestone.status === 'SUBMITTED' ? '更新交付物' : milestone.status === 'REVISION_REQUESTED' ? '重新提交' : '交付'}
          </Button>
        )}

        {canApprove && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onApproveMilestone?.(milestone.milestone_id)}
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            批准
          </Button>
        )}

        {canReqRevision && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRequestRevision?.(milestone.milestone_id)}
          >
            <XCircle className="w-4 h-4 mr-1" />
            请求修改
          </Button>
        )}

        {/* Request extension — only creator can request, only when milestone is in flight
            and there's no pending extension already. */}
        {!pendingExtension
          && milestone.status !== 'COMPLETED'
          && milestone.status !== 'CANCELLED'
          && currentUserId === contract.creator_user_id
          && onRequestExtension && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRequestExtension(milestone.milestone_id)}
          >
            <Clock className="w-4 h-4 mr-1" />
            申请延期
          </Button>
        )}
      </div>

      {/* Pending extension banner */}
      {pendingExtension && (
        <div className="mt-4 ml-11 rounded-lg border border-blue-200 bg-blue-50/60 p-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <p className="font-medium text-blue-900 mb-1">
                {currentUserId === contract.client_user_id ? '创作者申请延期' : '已提交延期申请'}
              </p>
              <p className="text-slate-700">
                完成日期 <span className="line-through text-slate-400">{formatDate(pendingExtension.oldCompletionDate)}</span>
                {' → '}
                <span className="font-medium">{formatDate(pendingExtension.newCompletionDate)}</span>
              </p>
              {pendingExtension.reason && (
                <p className="text-slate-600 mt-1">理由：{pendingExtension.reason}</p>
              )}
              {/* Only the client may approve / reject. Creator just sees waiting state. */}
              {currentUserId === contract.client_user_id ? (
                <div className="flex items-center gap-2 mt-3">
                  <Button variant="primary" size="sm" onClick={() => onApproveExtension?.(pendingExtension.amendmentId)}>
                    批准延期
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onRejectExtension?.(pendingExtension.amendmentId)}>
                    拒绝
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-slate-500 mt-2">等待客户批准...</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
